/**
 * CAPTCHA Widget Control
 * Implements math challenge CAPTCHA for form verification
 */

import React, { memo, useState, useEffect } from 'react';
import type { ControlRendererProps } from '../types';

interface CaptchaControlConfig {
  difficulty?: 'easy' | 'medium' | 'hard';
  type?: 'math' | 'image';
}

const CaptchaControl = memo(({ control, value, onChange, mode }: ControlRendererProps) => {
  const config = (control.config as CaptchaControlConfig) || {};
  const difficulty = config.difficulty ?? 'medium';
  const type = config.type ?? 'math';

  const [num1, setNum1] = useState(0);
  const [num2, setNum2] = useState(0);
  const [operation, setOperation] = useState('+');
  const [userAnswer, setUserAnswer] = useState('');
  const [verified, setVerified] = useState(false);

  // Generate challenge on mount and when difficulty changes
  useEffect(() => {
    generateChallenge();
  }, [difficulty]);

  const generateChallenge = () => {
    const difficultyMap = {
      easy: 10,
      medium: 50,
      hard: 100,
    };
    const max = difficultyMap[difficulty];
    const newNum1 = Math.floor(Math.random() * max) + 1;
    const newNum2 = Math.floor(Math.random() * max) + 1;
    const ops = ['+', '-', '*'];
    const newOp = ops[Math.floor(Math.random() * ops.length)];

    setNum1(newNum1);
    setNum2(newNum2);
    setOperation(newOp);
    setUserAnswer('');
    setVerified(false);
  };

  const getCorrectAnswer = () => {
    switch (operation) {
      case '+':
        return num1 + num2;
      case '-':
        return num1 - num2;
      case '*':
        return num1 * num2;
      default:
        return 0;
    }
  };

  const handleVerify = () => {
    const correct = getCorrectAnswer();
    if (parseInt(userAnswer) === correct) {
      setVerified(true);
      onChange?.('verified');
    } else {
      setUserAnswer('');
      generateChallenge();
    }
  };

  // In view or preview mode, just show if verified
  if (mode === 'view' || mode === 'preview') {
    return (
      <div style={{
        padding: '12px',
        backgroundColor: verified ? '#d4edda' : '#fff3cd',
        border: `1px solid ${verified ? '#28a745' : '#ffc107'}`,
        borderRadius: '4px',
        color: verified ? '#155724' : '#856404',
        fontSize: '14px',
      }}>
        {verified ? '✓ CAPTCHA Verified' : '⚠ CAPTCHA Not Verified'}
      </div>
    );
  }

  // In edit/design mode, show the challenge
  return (
    <div style={{
      padding: '16px',
      backgroundColor: '#f9f9f9',
      border: '1px solid #ddd',
      borderRadius: '4px',
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
    }}>
      <label style={{ fontWeight: '600', fontSize: '14px', color: '#333' }}>
        🔒 {control.label || 'CAPTCHA Verification'}
      </label>

      {type === 'math' && (
        <>
          <div style={{
            backgroundColor: '#fff',
            padding: '12px',
            borderRadius: '4px',
            border: '1px solid #ddd',
            fontSize: '16px',
            fontWeight: '600',
            textAlign: 'center',
            color: '#333',
          }}>
            {num1} {operation} {num2} = ?
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            <input
              type="number"
              value={userAnswer}
              onChange={(e) => setUserAnswer(e.target.value)}
              placeholder="Enter answer"
              style={{
                flex: 1,
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                boxSizing: 'border-box',
              }}
              disabled={verified}
            />
            <button
              onClick={handleVerify}
              disabled={!userAnswer || verified}
              style={{
                padding: '8px 16px',
                backgroundColor: verified ? '#28a745' : '#007bff',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: verified ? 'default' : 'pointer',
                fontSize: '14px',
                fontWeight: '500',
              }}
            >
              {verified ? '✓ Verified' : 'Verify'}
            </button>
          </div>

          {verified && (
            <div style={{
              backgroundColor: '#d4edda',
              color: '#155724',
              padding: '10px',
              borderRadius: '4px',
              fontSize: '13px',
              textAlign: 'center',
            }}>
              ✓ CAPTCHA verified successfully
            </div>
          )}
        </>
      )}

      <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
        Difficulty: {difficulty} | Type: {type}
      </div>
    </div>
  );
});

CaptchaControl.displayName = 'CaptchaControl';

export { CaptchaControl };
export type { CaptchaControlConfig };
